import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Columns3, Inbox, Rows3, Rows4, Search, X } from 'lucide-react';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useLive } from '@/lib/live';
import { useTablePrefs } from '@/lib/table-prefs';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { DropdownContent, DropdownLabel, DropdownMenu, DropdownTrigger } from './dropdown';
import { Input, Select } from './input';
import { EmptyState, Skeleton } from './misc';

/** Per-column options, set on a column's `meta`. */
export interface ColumnMeta<T> {
  /** Server-side sort key; the header becomes a sort button. */
  sortKey?: string;
  align?: 'right' | 'center';
  /** Stays in view when the table scrolls sideways (the voucher number). */
  sticky?: boolean;
  /** Cannot be hidden from the column chooser. */
  fixed?: boolean;
  /** Totals row: this page's figure, and the figure over everything matching the filter. */
  total?: { page?(rows: T[]): ReactNode; all?: ReactNode };
}

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[] | undefined;
  total: number;
  page: number;
  pageSize: number;
  loading?: boolean;
  /** Server-side sort: "field" or "-field". */
  sort?: string;
  onSortChange?(sort: string | undefined): void;
  onPageChange(page: number): void;
  onPageSizeChange?(size: number): void;
  search?: string;
  onSearchChange?(search: string): void;
  searchPlaceholder?: string;
  toolbar?: ReactNode;
  onRowClick?(row: T): void;
  empty?: ReactNode;
  /** Stores the person's column choice and density for this list (server-side, follows them to any computer). */
  tableId?: string;
  /** Columns (by header text or id) hidden until the person chooses their own. */
  defaultHidden?: string[];
  /** The filter panel (tinted, under the search bar). */
  filters?: ReactNode;
  /** Print / PDF / Excel buttons, at the right of the search bar. */
  exports?: ReactNode;
  /** Shown above the rows — e.g. "All 25 on this page are selected. Select all 312 matching". */
  banner?: ReactNode;
  /** What the colours and marks mean, under the table. */
  legend?: ReactNode;
  /** Row tint, e.g. selected rows. */
  rowClassName?(row: T): string | undefined;
}

const PAGE_SIZES = [25, 50, 100];
const colId = <T,>(c: ColumnDef<T, unknown>) => c.id ?? (typeof c.header === 'string' ? c.header : '');

/**
 * Server-paginated table: data arrives one page at a time, search is debounced. Clear rows and columns (grid lines,
 * alternate shading, header that stays in view), sort by column, a totals row, page numbers, and — with a table id —
 * the person's own column choice and density.
 */
export function DataTable<T>(p: DataTableProps<T>) {
  const liveIds = useLive((st) => st.recent);
  const { prefs, save } = useTablePrefs(p.tableId, p.defaultHidden);
  const hidden = new Set(prefs.hidden);
  const shown = p.columns.filter((c) => !hidden.has(colId(c)) || (c.meta as ColumnMeta<T> | undefined)?.fixed);
  const table = useReactTable({ data: p.data ?? [], columns: shown, getCoreRowModel: getCoreRowModel(), manualPagination: true, manualSorting: true });
  const pages = Math.max(1, Math.ceil(p.total / p.pageSize));
  const from = p.total === 0 ? 0 : (p.page - 1) * p.pageSize + 1;
  const to = Math.min(p.page * p.pageSize, p.total);
  const compact = prefs.density === 'compact';
  const meta = (c: ColumnDef<T, unknown>) => c.meta as ColumnMeta<T> | undefined;
  const hasTotals = shown.some((c) => meta(c)?.total);
  const hasAll = shown.some((c) => meta(c)?.total?.all != null);
  const rows = p.data ?? [];
  const cellCls = (m: ColumnMeta<T> | undefined) => cn('border-r border-border/60 px-3 last:border-r-0', m?.align === 'right' && 'text-right', m?.align === 'center' && 'text-center', m?.sticky && 'sticky left-0 z-[1] bg-inherit');
  const totalRow = (label: string, pick: (m: ColumnMeta<T>) => ReactNode) => (
    <tr className="bg-surface-2">
      {shown.map((c, i) => {
        const m = meta(c);
        return <td key={colId(c) || i} className={cn(cellCls(m), 'h-9 font-semibold whitespace-nowrap tabular-nums')}>{m?.total ? pick(m) : i === (colId(shown[0]!) === 'select' ? 1 : 0) ? <span className="text-[11px] font-semibold tracking-wide text-fg-muted uppercase">{label}</span> : null}</td>;
      })}
    </tr>
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-(--shadow-card)">
      {(p.onSearchChange || p.toolbar || p.exports || p.tableId) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
          {p.onSearchChange && <SearchBox value={p.search ?? ''} onChange={p.onSearchChange} placeholder={p.searchPlaceholder} wide={!!p.tableId} />}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {p.toolbar}
            {p.tableId && (
              <>
                <DropdownMenu>
                  <DropdownTrigger asChild><Button variant="secondary" size="sm" aria-label="Choose columns"><Columns3 /> Columns{prefs.hidden.length ? ` (${p.columns.length - shown.length} hidden)` : ''}</Button></DropdownTrigger>
                  <DropdownContent className="w-56">
                    <DropdownLabel>Columns to show</DropdownLabel>
                    {p.columns.filter((c) => colId(c) && colId(c) !== 'select' && !meta(c)?.fixed).map((c) => {
                      const id = colId(c);
                      return (
                        <label key={id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px] hover:bg-surface-2">
                          <input type="checkbox" checked={!hidden.has(id)} onChange={(e) => save({ hidden: e.target.checked ? prefs.hidden.filter((h) => h !== id) : [...prefs.hidden, id] })} />
                          {id}
                        </label>
                      );
                    })}
                    {prefs.hidden.length > 0 && <button type="button" className="mt-1 w-full rounded px-2 py-1.5 text-left text-[12.5px] text-primary hover:bg-surface-2" onClick={() => save({ hidden: [] })}>Show all columns</button>}
                  </DropdownContent>
                </DropdownMenu>
                <Button variant="secondary" size="sm" onClick={() => save({ density: compact ? 'comfortable' : 'compact' })} title={compact ? 'Roomier rows' : 'More rows on screen'} aria-label="Row density">
                  {compact ? <Rows3 /> : <Rows4 />} {compact ? 'Compact' : 'Comfortable'}
                </Button>
              </>
            )}
            {p.exports}
          </div>
        </div>
      )}
      {p.filters && <div className="border-b border-border bg-primary-soft/35 px-3 py-2.5">{p.filters}</div>}
      {p.banner}
      <div className={cn('overflow-auto', p.tableId && 'max-h-[calc(100vh-13rem)]')}>
        <table className={cn('w-full border-separate border-spacing-0', compact ? 'text-[12.5px]' : 'text-sm', p.loading && p.data && 'fx-reloading')}>
          <thead className="fx-sticky-head sticky top-0 z-[2] text-left text-[11px] font-semibold tracking-wide text-fg-muted uppercase">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="bg-surface-2">
                {hg.headers.map((h) => {
                  const m = h.column.columnDef.meta as ColumnMeta<T> | undefined;
                  const active = !!m?.sortKey && p.sort?.replace('-', '') === m.sortKey;
                  const desc = p.sort?.startsWith('-');
                  return (
                    <th key={h.id} className={cn(cellCls(m), 'h-10 border-b border-primary/15 bg-[color-mix(in_oklab,var(--color-primary)_6%,var(--color-surface))] font-bold whitespace-nowrap text-fg-muted', m?.sticky && 'z-[3]')} style={{ width: h.getSize() !== 150 ? h.getSize() : undefined }}>
                      {m?.sortKey && p.onSortChange ? (
                        <button type="button" className={cn('group/th inline-flex items-center gap-1 uppercase transition-colors hover:text-fg', active && 'text-primary', m.align === 'right' && 'flex-row-reverse')} onClick={() => p.onSortChange!(!active ? m.sortKey : desc ? undefined : `-${m.sortKey}`)} title="Sort">
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {active ? (desc ? <ArrowDown className="fx-pop size-3" /> : <ArrowUp className="fx-pop size-3" />) : <ArrowUpDown className="size-3 opacity-40 transition-opacity group-hover/th:opacity-80" />}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {/* A new page, sort or search replays the row wave; a background refresh of the same rows does not. */}
          <tbody key={`${p.page}|${p.sort ?? ''}|${p.search ?? ''}|${p.loading && !p.data ? 'l' : 'd'}`} className="fx-rows">
            {p.loading && !p.data
              ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {shown.map((_, j) => (
                      <td key={j} className="h-10 border-b border-border/60 px-3">
                        <Skeleton className="h-3.5" style={{ width: `${45 + ((i * 7 + j * 13) % 45)}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              : table.getRowModel().rows.map((row, i) => (
                  <tr
                    key={row.id}
                    onClick={p.onRowClick ? () => p.onRowClick!(row.original) : undefined}
                    style={{ '--i': i } as CSSProperties}
                    className={cn(
                      'group transition-colors duration-150',
                      i % 2 ? 'bg-surface-2/60' : 'bg-surface',
                      p.onRowClick && 'cursor-pointer hover:bg-primary-soft/60 [&>td:first-child]:shadow-[inset_3px_0_0_transparent] [&>td:first-child]:transition-shadow hover:[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-primary)] active:bg-primary-soft',
                      p.rowClassName?.(row.original),
                      liveIds[(row.original as { id?: string }).id ?? ''] && 'fx-flash',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={cn(cellCls(cell.column.columnDef.meta as ColumnMeta<T> | undefined), 'border-b border-border/60 whitespace-nowrap', compact ? 'h-8' : 'h-11')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
          {hasTotals && rows.length > 0 && (
            <tfoot className="sticky bottom-0 z-[2] border-t-2 border-border-strong">
              {totalRow('This page', (m) => m.total?.page?.(rows))}
              {hasAll && pages > 1 && totalRow(`All ${p.total.toLocaleString('en-US')} matching`, (m) => m.total?.all)}
            </tfoot>
          )}
        </table>
        {!p.loading && p.data?.length === 0 && (p.empty ?? <EmptyState icon={<Inbox />} title="No records found" description={p.search ? 'Try a different search term.' : undefined} />)}
      </div>
      <div className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border bg-surface-2/50 px-3 py-2 text-[13px] text-fg-muted', p.loading && p.data && 'opacity-70')}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="tabular">
            Showing <b className="font-semibold text-fg">{from.toLocaleString('en-US')}–{to.toLocaleString('en-US')}</b> of <b className="font-semibold text-fg">{p.total.toLocaleString('en-US')}</b>
          </span>
          {p.legend}
        </div>
        <div className="flex items-center gap-1.5">
          {p.onPageSizeChange && (
            <Select className="mr-1 h-8 w-auto text-[13px]" value={p.pageSize} onChange={(e) => p.onPageSizeChange!(Number(e.target.value))} aria-label="Rows per page">
              {[...new Set([...PAGE_SIZES, p.pageSize])].sort((a, b) => a - b).map((n) => (
                <option key={n} value={n}>
                  {n} per page
                </option>
              ))}
            </Select>
          )}
          <Button variant="secondary" size="icon-sm" disabled={p.page <= 1} onClick={() => p.onPageChange(1)} aria-label="First page"><ChevronsLeft /></Button>
          <Button variant="secondary" size="icon-sm" disabled={p.page <= 1} onClick={() => p.onPageChange(p.page - 1)} aria-label="Previous page"><ChevronLeft /></Button>
          {pageNumbers(p.page, pages).map((n, i) =>
            n == null ? <span key={`gap${i}`} className="px-1 text-fg-subtle">…</span> : (
              <button key={n} type="button" onClick={() => p.onPageChange(n)} aria-current={n === p.page ? 'page' : undefined}
                className={cn('hidden h-8 min-w-8 rounded-md px-2 text-[13px] tabular-nums transition-all duration-200 sm:inline-block', n === p.page ? 'fx-pop bg-primary font-semibold text-primary-fg shadow-[0_4px_10px_-4px_color-mix(in_oklab,var(--color-primary)_60%,transparent)]' : 'hover:-translate-y-px hover:bg-surface hover:text-fg hover:shadow-(--shadow-card)')}>{n}</button>
            ),
          )}
          <span className="tabular px-1 sm:hidden">{p.page} / {pages}</span>
          <Button variant="secondary" size="icon-sm" disabled={p.page >= pages} onClick={() => p.onPageChange(p.page + 1)} aria-label="Next page"><ChevronRight /></Button>
          <Button variant="secondary" size="icon-sm" disabled={p.page >= pages} onClick={() => p.onPageChange(pages)} aria-label="Last page"><ChevronsRight /></Button>
        </div>
      </div>
    </div>
  );
}

/** 1 … 4 5 6 … 20 — the first, last and the pages either side of the current one. */
function pageNumbers(page: number, pages: number): (number | null)[] {
  const want = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
  const out: (number | null)[] = [];
  let last = 0;
  for (const n of [...want].sort((a, b) => a - b)) {
    if (n - last > 1) out.push(null);
    out.push(n);
    last = n;
  }
  return out;
}

/** Debounced search input (300 ms) to avoid an API call per keystroke. */
export function SearchBox({ value, onChange, placeholder = 'Search…', wide }: { value: string; onChange(v: string): void; placeholder?: string; wide?: boolean }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  useEffect(() => {
    if (local === value) return;
    const t = setTimeout(() => onChange(local.trim()), 300);
    return () => clearTimeout(t);
  }, [local, value, onChange]);
  return (
    // As wide as the hint needs (up to 30rem), so the hint is never cut off.
    <div className="relative w-full" style={{ maxWidth: `${Math.max(wide ? 30 : 18, Math.min(30, placeholder.length * 0.5 + 4))}rem` }}>
      <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-subtle" />
      <Input value={local} onChange={(e) => setLocal(e.target.value)} placeholder={placeholder} className="h-8 pr-8 pl-8" aria-label="Search" title={placeholder} />
      {local && (
        <button type="button" className="absolute top-1/2 right-2 -translate-y-1/2 text-fg-subtle hover:text-fg" onClick={() => setLocal('')} aria-label="Clear search">
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}
